<script setup lang="ts">
import { storeToRefs } from 'pinia';

import { Button } from '@clovyr/bed';
import ImageMedia from '@clovyr/bed/components/media/ImageMedia.vue';
import { type ComputeProviderRegion } from '@clovyr/pollen';

import EditIcon from '@/components/icons/EditIcon.vue';
import { useLauncherStore } from '@/stores/launcher_store';

import { useUserFlagsStore } from '../../stores/user_flags';
import ExpandedDropdown from '../blocks/ExpandedDropdown.vue';

const { canAccessPreviewFeatures } = storeToRefs(useUserFlagsStore());

const launcherStore = useLauncherStore();
const { selectedHost, deployment, manifest } = storeToRefs(launcherStore);

// Computed property for regionOptions
const regionOptions = computed<ComputeProviderRegion[]>(() => {
  if (selectedHost.value === undefined) {
    throw new Error('selectedHost is undefined');
  }
  return selectedHost.value.regions;
});

type ComputeProviderSize = {
  size: string;
  memory: string;
  storage: string;
  CPU: string;
};

const serverSizes = [
  {
    size: 'Micro',
    memory: '512MB',
    storage: '10GB',
    CPU: '1 vCPU',
  },
  {
    size: 'Small',
    memory: '1GB',
    storage: '25GB',
    CPU: '1 vCPU',
  },
  {
    size: 'Medium',
    memory: '2GB',
    storage: '50GB',
    CPU: '1 vCPUs',
  },
  {
    size: 'Large',
    memory: '8GB',
    storage: '160GB',
    CPU: '4 vCPUs',
  },
  {
    size: 'XLarge',
    memory: '16GB',
    storage: '320GB',
    CPU: '8 vCPUs',
  },
  {
    size: 'XLargeAI',
    memory: '96GB',
    storage: '1.2TB',
    CPU: '48 vCPUs',
  },
];

// Computed property for sizeOptions
const sizeOptions = computed<ComputeProviderSize[]>(() => {
  // if (selectedHost.value === undefined) {
  //   throw new Error('selectedHost is undefined');
  // }
  // return selectedHost.value.size;

  return serverSizes;
});

// Computed property for selectedRegion
const selectedRegion = computed(() => {
  return regionOptions.value.find((x) => x.value === deployment.value.region)!;
});
// Computed property for selectedSize
const selectedSize = computed(() => {
  // User's selection if set, otherwise fall back to manifest
  const userSize = deployment.value.size;
  if (userSize && sizeOptions.value.some((x) => x.size === userSize)) {
    return sizeOptions.value.find((x) => x.size === userSize)!;
  }
  // Fallback to manifest
  const manifestSize = manifest.value.deployment.compute?.size;
  if (
    manifestSize &&
    sizeOptions.value.some((x) => x.size.toLowerCase() === manifestSize.toLowerCase())
  ) {
    return sizeOptions.value.find((x) => x.size.toLowerCase() === manifestSize.toLowerCase())!;
  }
  // Default to 'Small'
  return sizeOptions.value.find((x) => x.size === 'Small')!;
});

async function onChangeRegion(option) {
  if (option && option.value) {
    deployment.value.region = option.value;
  }
}
async function onChangeSize(option) {
  if (option && option.size) {
    deployment.value.size = option.size;
  }
}
const isEditingRegion = ref(false);
const isEditingSize = ref(false);

const onClickEdit = (option) => {
  if (option === 'region') {
    isEditingRegion.value = true;
  }
  if (option === 'size') {
    isEditingSize.value = true;
  }
};

const onClickButton = (button) => {
  if (button === 'cancel') {
    if (isEditingRegion.value) {
      isEditingRegion.value = false;
    }
    if (isEditingSize.value) {
      isEditingSize.value = false;
    }
  }
  if (button === 'save') {
    isEditingRegion.value = false;
    isEditingSize.value = false;
  }
};

const selectedRegionFlag = computed(() => {
  if (!selectedRegion.value || !selectedRegion.value.flag) {
    return '';
  }
  return `${selectedRegion.value.flag}-flag.svg`;
});
</script>

<template>
  <div class="regions">
    <div v-if="!isEditingRegion" class="region-display">
      <div class="selectinput-block__label-holder">
        <div class="selectinput-block__label">Server Location:</div>
        <div class="regions-spread">
          <div class="regions-details">
            <div class="regions-flag"><ImageMedia :filename="selectedRegionFlag" /></div>
            <div class="regions-name">{{ selectedRegion.name }}</div>
          </div>
          <div class="edit-icon" @click="onClickEdit('region')"><EditIcon /></div>
        </div>
      </div>
    </div>
    <!-- dropdown when editing -->
    <ExpandedDropdown
      v-if="isEditingRegion"
      :model-value="selectedRegion"
      :options="regionOptions"
      @selected="onChangeRegion"
    >
      <template #value="{ value }: { value: ComputeProviderRegion }">
        <div class="selectinput-block">
          <div class="selectinput-block__label-holder">
            <div class="selectinput-block__label">Server Location:</div>
          </div>
          <div v-if="isEditingRegion" class="selectinput-block__input">
            <ImageMedia :filename="`${value.flag}-flag.svg`" class="regions-flag" />{{ value.name }}
          </div>
        </div>
      </template>

      <template #options="{ option }: { option: ComputeProviderRegion }">
        <div class="regions-option">
          <ImageMedia :filename="`${option.flag}-flag.svg`" class="regions-flag" />{{ option.name }}
        </div>
      </template>
    </ExpandedDropdown>
  </div>
  <div class="size" v-if="canAccessPreviewFeatures">
    <!-- read-only review: show selected size -->
    <div v-if="!isEditingSize" class="region-display">
      <div class="selectinput-block__label-holder">
        <div class="selectinput-block__label">Server Size:</div>
        <div class="regions-spread">
          <div class="regions-details">
            <div class="size-info">
              <p>{{ selectedSize.size }}</p>
              <div>
                {{ selectedSize.memory }} / {{ selectedSize.storage }} / {{ selectedSize.CPU }}
              </div>
            </div>
          </div>
          <div class="edit-icon" @click="onClickEdit('size')"><EditIcon /></div>
        </div>
      </div>
    </div>
    <!-- dropdown when editing -->
    <ExpandedDropdown
      v-if="isEditingSize"
      :model-value="selectedSize"
      :options="sizeOptions"
      @selected="onChangeSize"
    >
      <template #value="{ value }: { value: ComputeProviderSize }">
        <div class="selectinput-block">
          <div class="selectinput-block__label-holder">
            <div class="selectinput-block__label">Server Size:</div>
          </div>
          <div v-if="isEditingSize" class="selectinput-block__input">
            <div class="size-info">
              <p>{{ value.size }}</p>
              <div>{{ value.memory }} / {{ value.storage }} / {{ value.CPU }}</div>
            </div>
          </div>
        </div>
      </template>

      <template #options="{ option }: { option: ComputeProviderSize }">
        <div class="regions-option">
          <div class="size-info">
            <p>{{ option.size }}</p>
            <div>{{ option.memory }} / {{ option.storage }} / {{ option.CPU }}</div>
          </div>
        </div>
      </template>
    </ExpandedDropdown>
  </div>
  <div v-if="isEditingRegion || isEditingSize" class="save-buttons">
    <Button label="cancel" type="submit" @click="onClickButton('cancel')" />
    <Button label="save" type="submit" reversed @click="onClickButton('save')" />
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.regions,
.size {
  margin-top: 1rem;
  padding: 0.5rem;

  .selectinput-block__label-holder {
    margin-bottom: 0.25rem;
    padding-left: 1.5rem;
  }
  .selectinput-block__input {
    display: flex;
    align-items: center;
    padding-left: 1.5rem;
    cursor: pointer;
  }
  .region-option {
    padding: 0.1rem 0.75rem;
  }
  .region-option:hover {
    background-color: colors.color(grey, primary);
  }
  :deep(.dropdown-container) {
    margin-top: 0.15rem;
    transform: translate(5px);
    border: unset;
    padding: unset;
    cursor: pointer;
    position: absolute;
    z-index: 10;
    width: 100%;
    max-width: 100vw;
    overflow: hidden;
    min-width: 100%;
    max-width: 100vw;
    left: 0;

    @include breakpoints.media-breakpoint-up(sm) {
      width: 23rem;
      left: auto; // Reset mobile override
    }
  }
  :deep(.dropdown-items) {
    width: 100%;
    max-width: 100vw;
    max-height: 40vh; // or adjust as needed
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;

    @include breakpoints.media-breakpoint-up(sm) {
      max-height: 21.5rem;
    }
  }
  .regions-spread {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;

    .edit-icon {
      cursor: pointer;
    }
  }
  .regions-details {
    display: flex;
  }
  .regions-flag {
    margin-right: 0.75rem;
  }
  .regions-option {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.save-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
}
.selectinput-block__label {
  padding-left: 0;
}
.regions {
  position: relative;
  z-index: 2;
}
.size {
  margin-top: 1rem;

  .size-info {
    display: flex;

    p {
      font-weight: bold;
      margin-right: 0.5rem;
    }
    div {
      color: colors.color(grey, secondary);
    }
  }
}
</style>
